// src/views/Payments.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import './Payments.css';
import { Link } from 'react-router-dom';
import {
  FaEdit, FaTrash, FaUndo, FaSearch, FaTimes, FaSort, FaSortUp, FaSortDown, FaHistory, FaPrint, FaDownload
} from 'react-icons/fa';

const API = import.meta.env.VITE_API_URL;
const DEFAULT_LIMIT = 50;

// ---------- Date preset helpers (Mon-Sun week) ----------
const fmtYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${da}`;
};
const startOfWeekMon = (d) => {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = x.getDay(); // 0=Sun..6=Sat
  const diff = (day === 0 ? -6 : 1 - day); // Monday as first day
  x.setDate(x.getDate() + diff);
  return x;
};
const endOfWeekMon = (d) => { const s = startOfWeekMon(d); const e = new Date(s); e.setDate(e.getDate() + 6); return e; };
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const startOfYear = (y) => new Date(y, 0, 1);
const endOfYear = (y) => new Date(y, 11, 31);

const emptyForm = {
  id: null,
  clientid: '',
  invoiceid: '',
  date: '',
  paymenttype: '',
  amt: '',
  reference: '',
  notes: '',
  user: '',
  userFullName: '',
};

const defaultMeta = {
  total: 0,
  limit: DEFAULT_LIMIT,
  offset: 0,
  sumAmt: 0,
};

function getUserFullName() {
  try {
    const u =
      JSON.parse(localStorage.getItem('currentUser')) ||
      JSON.parse(localStorage.getItem('user')) ||
      {};
    return u.fullname || u.userFullName || u.fullName || u.name || u.login || '';
  } catch {
    return '';
  }
}

const Payments = () => {
  // table rows + meta
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(defaultMeta);
  const [loading, setLoading] = useState(false);
  const [fatalError, setFatalError] = useState(null);

  // filters
  const getInitialLimit = () => {
    const stored = window.localStorage.getItem('paymentsLimit');
    return stored ? Math.min(500, Math.max(1, parseInt(stored, 10))) : DEFAULT_LIMIT;
  };
  const [limit, setLimit] = useState(getInitialLimit());
  const [limitInput, setLimitInput] = useState(String(getInitialLimit()));
  const [isTypingLimit, setIsTypingLimit] = useState(false);

  const getInitialSort = () => {
    try {
      return JSON.parse(localStorage.getItem('paymentsSort') || '{"sortField":"date","sortOrder":"DESC"}');
    } catch { return { sortField: 'date', sortOrder: 'DESC' }; }
  };
  const [sortField, setSortField] = useState(getInitialSort().sortField);
  const [sortOrder, setSortOrder]   = useState(getInitialSort().sortOrder);

  const [deleted, setDeleted] = useState('0'); // keep for API compatibility, but column is removed
  const [clientName, setClientName] = useState(localStorage.getItem('paymentsClientName') || '');
  const [invoiceid, setInvoiceid] = useState('');
  const [from, setFrom] = useState(localStorage.getItem('paymentsFrom') || '');
  const [to, setTo] = useState(localStorage.getItem('paymentsTo') || '');
  const [q, setQ] = useState(localStorage.getItem('paymentsQ') || '');
  const [datePreset, setDatePreset] = useState(() => localStorage.getItem('paymentsDatePreset') || '');
  const [paymentTypeFilter, setPaymentTypeFilter] = useState(() => localStorage.getItem('paymentsTypeId') || '');

  // edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  // history (for edit modal)
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyForId, setHistoryForId] = useState(null);

  // payment types
  const [paymentTypes, setPaymentTypes] = useState([]);

  // clients & invoices for NEW payment
  const [clients, setClients] = useState([]);
  const [openInvoices, setOpenInvoices] = useState([]);

  // invoice hover popover control (so it doesn't disappear when moving mouse)
  const [hoverPayId, setHoverPayId] = useState(null);
  const hoverTimerRef = useRef(null);

  const USD = new Intl.NumberFormat('en-US', { 
    style: 'currency', 
    currency: 'USD', 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  });
  const money = (v) => USD.format(Number.isFinite(+v) ? +v : 0);

  const displayInvoiceId = (id) =>
    id === -1 || id === null || id === undefined ? 'None' : id;

  const fetchJSON = async (url, opts) => {
    const res = await fetch(url, opts);
    if (!res.ok) {
      let msg = `${res.status} ${res.statusText}`;
      try {
        const body = await res.json();
        if (body?.error) msg = body.error;
      } catch (_) {}
      throw new Error(msg);
    }
    return res.json();
  };

  const fetchPaymentTypes = async () => {
    try {
      const json = await fetchJSON(`${API}/api/paymenttypes`);
      setPaymentTypes(Array.isArray(json) ? json : []);
    } catch (e) {
      console.error('Failed to fetch payment types', e);
      setPaymentTypes([]);
    }
  };

  const fetchClients = async () => {
    try {
      const json = await fetchJSON(`${API}/api/clients/minimal`);
      setClients(Array.isArray(json) ? json : []);
    } catch (e) {
      console.error('Failed to fetch clients', e);
      setClients([]);
    }
  };

  const fetchOpenInvoices = async (cid) => {
    if (!cid) {
      setOpenInvoices([]);
      return;
    }
    try {
      const json = await fetchJSON(`${API}/api/invoices/open?clientid=${cid}`);
      setOpenInvoices(Array.isArray(json) ? json : []);
    } catch (e) {
      console.error('Failed to fetch open invoices', e);
      setOpenInvoices([]);
    }
  };

  const fetchPayments = async (offset = 0, customLimit = limit) => {
    setLoading(true);
    setFatalError(null);
    try {
      const params = new URLSearchParams({
        sortField,
        sortOrder,
        limit: customLimit,
        offset,
        deleted, // server uses this even if we don't show the column
      });

      if (invoiceid) {
        params.set('invoiceid', invoiceid);
        // Treat input as either invoice # or payment #
        params.set('paymentid', invoiceid);
      }
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      if (q) params.set('q', q);
      if (clientName) {
        // Prefer explicit server support if present…
        params.set('clientname', clientName);
        // …but also backstop with q so older routes still match by c.name
        if (!q) params.set('q', clientName);
      }
      if (paymentTypeFilter) params.set('paymenttype', String(paymentTypeFilter));

      const json = await fetchJSON(`${API}/api/payments?${params.toString()}`);

      const list = Array.isArray(json) ? json : (json.data || []);
      const nextMeta = Array.isArray(json)
        ? {
            total: list.length,
            sumAmt: list.reduce((s, r) => s + Number(r.amt || 0), 0),
            limit: customLimit,
            offset,
          }
        : (json.meta || { total: 0, sumAmt: 0, limit: customLimit, offset });

      setRows(list);
      setMeta(nextMeta);
    } catch (err) {
      console.error('Failed to fetch payments:', err);
      setFatalError(err.message || 'Failed to load payments');
      setRows([]);
      setMeta({ total: 0, sumAmt: 0, limit: customLimit, offset });
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async (paymentId) => {
    setHistoryLoading(true);
    try {
      const json = await fetchJSON(`${API}/api/payments/${paymentId}/history`);
      setHistory(Array.isArray(json) ? json : []);
    } catch (e) {
      console.error('Failed to fetch payment history', e);
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const openHistory = async (p) => {
    if (!p?.id) return;
    setHistory([]);
    setHistoryForId(p.id);
    setHistoryOpen(true);
    try { await fetchHistory(p.id); } catch {}
  };

  useEffect(() => {
    fetchPaymentTypes();
    fetchClients();
  }, []);

  useEffect(() => {
    fetchPayments(meta.offset, limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortOrder, deleted, from, to, q, clientName, limit, paymentTypeFilter]);

  // Debounce invoice number filtering while typing
  const invoiceDebounceRef = useRef(null);
  useEffect(() => {
    if (invoiceDebounceRef.current) clearTimeout(invoiceDebounceRef.current);
    invoiceDebounceRef.current = setTimeout(() => {
      fetchPayments(0, limit);
    }, 350);
    return () => { if (invoiceDebounceRef.current) clearTimeout(invoiceDebounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceid]);

  // when creating a NEW payment and client changes, load their open invoices
  useEffect(() => {
    if (editOpen && !form.id) {
      fetchOpenInvoices(form.clientid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.clientid, editOpen]);

  useEffect(() => {
    localStorage.setItem('paymentsSort', JSON.stringify({ sortField, sortOrder }));
  }, [sortField, sortOrder]);

  useEffect(() => { localStorage.setItem('paymentsQ', q); }, [q]);
  useEffect(() => { localStorage.setItem('paymentsFrom', from); }, [from]);
  useEffect(() => { localStorage.setItem('paymentsTo', to); }, [to]);
  useEffect(() => { localStorage.setItem('paymentsClientName', clientName); }, [clientName]);
  useEffect(() => { localStorage.setItem('paymentsDatePreset', datePreset); }, [datePreset]);
  useEffect(() => { localStorage.setItem('paymentsTypeId', paymentTypeFilter || ''); }, [paymentTypeFilter]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortField(field);
      setSortOrder('ASC');
    }
  };

  // --- Topbar title (set after render; clear on unmount) ---
  useEffect(() => {
    const subtitle = meta?.total > 0 ? `(${Number(meta.total).toLocaleString()} found)` : '';
    window.dispatchEvent(new CustomEvent('topbar:setTitle', {
      detail: { title: 'Payments ', subtitle }
    }));
    return () => {
      window.dispatchEvent(new CustomEvent('topbar:clearTitle'));
    };
  }, [meta?.total]);

  const exportCsv = () => {
    const params = new URLSearchParams({
      sortField,
      sortOrder,
      deleted,
    });
    if (invoiceid) params.set('invoiceid', invoiceid);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (q) params.set('q', q);
    if (clientName) params.set('clientname', clientName);
    if (paymentTypeFilter) params.set('paymenttype', String(paymentTypeFilter));

    window.open(`${API}/api/payments/export?${params.toString()}`, '_blank');
  };

  // Date presets (Receipts-style)
  const applyDatePreset = (preset) => {
    const today = new Date();
    let f = '', t = '';

    switch (preset) {
      case 'all': { f = ''; t = ''; break; }
      case 'today': { const d = fmtYMD(today); f = d; t = d; break; }
      case 'yesterday': { const y = new Date(today); y.setDate(y.getDate() - 1); const d = fmtYMD(y); f = d; t = d; break; }
      case 'this_week': { const s = startOfWeekMon(today); f = fmtYMD(s); t = fmtYMD(today); break; }
      case 'last_week': { const s0 = startOfWeekMon(today); const s = new Date(s0); s.setDate(s0.getDate() - 7); const e = new Date(s); e.setDate(s.getDate() + 6); f = fmtYMD(s); t = fmtYMD(e); break; }
      case 'this_month': { const s = startOfMonth(today); const e = endOfMonth(today); f = fmtYMD(s); t = fmtYMD(e); break; }
      case 'last_month': { const s = startOfMonth(new Date(today.getFullYear(), today.getMonth() - 1, 1)); const e = endOfMonth(s); f = fmtYMD(s); t = fmtYMD(e); break; }
      case 'this_year': { const s = startOfYear(today.getFullYear()); f = fmtYMD(s); t = fmtYMD(today); break; }
      case 'last_year': { const y = today.getFullYear() - 1; f = fmtYMD(startOfYear(y)); t = fmtYMD(endOfYear(y)); break; }
      default: {
        setDatePreset('');
        localStorage.setItem('paymentsDatePreset', '');
        return;
      }
    }

    setFrom(f); setTo(t); setDatePreset(preset);
    localStorage.setItem('paymentsDatePreset', preset);
    fetchPayments(0, limit);
  };

  const page = useMemo(
    () => Math.floor((meta.offset || 0) / (limit || DEFAULT_LIMIT)) + 1,
    [meta.offset, limit]
  );
  const lastPage = useMemo(
    () => Math.max(1, Math.ceil((meta.total || 0) / (limit || DEFAULT_LIMIT))),
    [meta.total, limit]
  );

  const gotoPage = (p) => {
    const nextOffset = Math.max(0, (p - 1) * limit);
    fetchPayments(nextOffset, limit);
  };

  const openEdit = (row) => {
    const invId = row.invoiceId ?? row.invoiceid;
    setForm({
      id: row.id,
      clientid: row.clientid,
      invoiceid: invId,
      date: row.date ? String(row.date).slice(0, 10) : '',
      paymenttype: row.paymenttype, // id
      amt: row.amt,
      reference: row.reference || '',
      notes: row.notes || '',
      user: row.user || '',
      userFullName: row.userFullName || '',
    });
    setOpenInvoices([]); // not needed when editing
    setEditOpen(true);
    fetchHistory(row.id);
  };

  const openNew = () => {
    const fullName = getUserFullName();
    setForm({
      ...emptyForm,
      date: new Date().toISOString().slice(0, 10),
      userFullName: fullName,
      user: '',
    });
    setOpenInvoices([]);
    setHistory([]);
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setForm(emptyForm);
    setHistory([]);
  };

  const savePayment = async (e) => {
    e.preventDefault();
    try {
      const method = form.id ? 'PUT' : 'POST';
      const url = form.id ? `${API}/api/payments/${form.id}` : `${API}/api/payments`;

      const body = {
        clientid: form.clientid,
        invoiceid: form.invoiceid || null,
        date: form.date,
        paymenttype: form.paymenttype,
        amt: form.amt,
        reference: form.reference,
        notes: form.notes,
        user: form.user,
        userFullName: form.userFullName || getUserFullName() || 'system',
      };

      await fetchJSON(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      closeEdit();
      fetchPayments(meta.offset, limit);
    } catch (e) {
      console.error(e);
      alert(e.message);
    }
  };

  const deletePayment = async (id) => {
    const confirmDel = window.confirm('Soft delete this payment?');
    if (!confirmDel) return;
    try {
      const params = new URLSearchParams({
        userFullName: form.userFullName || getUserFullName() || 'system',
      });
      await fetchJSON(`${API}/api/payments/${id}?${params.toString()}`, {
        method: 'DELETE',
      });
      fetchPayments(meta.offset, limit);
    } catch (e) {
      console.error(e);
      alert('Delete failed.');
    }
  };

  const restorePayment = async (id) => {
    try {
      await fetchJSON(`${API}/api/payments/${id}/restore`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userFullName: getUserFullName() || 'system' }),
      });
      fetchPayments(meta.offset, limit);
    } catch (e) {
      console.error(e);
      alert('Restore failed.');
    }
  };

  const renderSortHeader = (label, field) => {
    const active = sortField === field;
    const icon = !active ? <FaSort style={{ opacity: 0.35, marginLeft: 6 }} /> :
      (sortOrder === 'ASC'
        ? <FaSortUp style={{ color: 'var(--primary-color)', marginLeft: 6 }} />
        : <FaSortDown style={{ color: 'var(--primary-color)', marginLeft: 6 }} />);
    return (
      <th
        onClick={() => handleSort(field)}
        className={active ? 'active-sort' : ''}
        style={{ cursor: 'pointer' }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          {label} {icon}
        </span>
      </th>
    );
  };

  const paymentTypeNameById = (id) => {
    const p = paymentTypes.find((t) => t.id === Number(id));
    return p ? p.name : id;
  };

  // hover popover helpers
  const startHover = (id) => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setHoverPayId(id);
  };
  const endHover = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => setHoverPayId(null), 120); // small delay feels nicer
  };

  const popoverStyles = {
    container: {
      position: 'relative',
      display: 'inline-block',
    },
    bubble: {
      position: 'absolute',
      top: '100%',
      left: 0,
      marginTop: 6,
      minWidth: 220,
      maxWidth: 360,
      maxHeight: 260,
      overflow: 'auto',
      background: 'var(--card-background)',
      color: 'var(--text-color)',
      border: '1px solid #444',
      borderRadius: 8,
      boxShadow: '0 10px 28px rgba(0,0,0,.35)',
      padding: '10px 12px',
      zIndex: 10,
    },
    chip: {
      display: 'inline-block',
      background: 'var(--input-background)',
      border: '1px solid #444',
      borderRadius: 999,
      padding: '2px 8px',
      margin: '4px 6px 0 0',
      textDecoration: 'none',
      color: 'var(--primary-color)',
      whiteSpace: 'nowrap',
    },
    faded: { opacity: 0.7 }
  };

  const handleLimitUpdate = (raw) => {
    let v = parseInt(raw ?? limitInput, 10);
    if (isNaN(v)) v = limit;
    if (v < 1) v = 1;
    if (v > 500) v = 500;

    setLimit(v);
    setLimitInput(String(v));
    window.localStorage.setItem('paymentsLimit', v);
    fetchPayments(0, v);
  };

  // -------- Print: payment + history + associated invoices --------
  const escapeHtml = (s) => String(s ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

  const buildPaymentPrintHtml = ({ payment, history = [], invoiceIds = [] }) => {
    const title = `Payment #${payment.id}`;
    const css = `
      html, body { margin: 0; padding: 0; }
      body { font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif; color: #111; }
      .wrap { padding: 20px; }
      h1 { margin: 0 0 8px; font-size: 20px; }
      h2 { margin: 14px 0 8px; font-size: 16px; }
      .grid { display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 6px 12px; }
      .muted { opacity: 0.8; }
      table { width: 100%; border-collapse: collapse; }
      th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; }
      th { background: #f4f4f4; }
      ul { margin: 6px 0; padding-left: 18px; }
      .right { text-align: right; }
    `;
    const invList = (invoiceIds && invoiceIds.length)
      ? `<ul>${invoiceIds.map(id => `<li>Invoice #${id}</li>`).join('')}</ul>`
      : `<div class="muted">None</div>`;

    const histRows = (history || []).map(h => `
      <tr>
        <td>${escapeHtml(new Date(h.date).toLocaleString())}</td>
        <td>${escapeHtml(h.userFullName || '')}</td>
        <td>${escapeHtml(h.log || '')}</td>
      </tr>`).join('') || `<tr><td colspan="3" class="muted">No history</td></tr>`;

    return `<!doctype html><html><head><meta charset="utf-8" />
      <title>${escapeHtml(title)}</title>
      <style>${css}</style></head>
      <body><div class="wrap">
        <h1>${escapeHtml(title)}</h1>
        <div class="grid">
          <div><strong>Client:</strong> ${escapeHtml(payment.clientName || '')} (#${escapeHtml(payment.clientid)})</div>
          <div><strong>Date:</strong> ${escapeHtml(new Date(payment.date).toLocaleString())}</div>
          <div><strong>Amount:</strong> ${escapeHtml(money(payment.amt))}</div>
          <div><strong>Type:</strong> ${escapeHtml(payment.paymentTypeName || paymentTypeNameById(payment.paymenttype))}</div>
          <div><strong>Reference:</strong> ${escapeHtml(payment.reference || '')}</div>
          <div><strong>Created by:</strong> ${escapeHtml(payment.userFullName || payment.user || '')}</div>
          <div style="grid-column: 1 / -1"><strong>Notes:</strong> ${escapeHtml(payment.notes || '')}</div>
        </div>

        <h2>Applied Invoices</h2>
        ${invList}

        <h2>History</h2>
        <table>
          <thead><tr><th>Date</th><th>User</th><th>Log</th></tr></thead>
          <tbody>${histRows}</tbody>
        </table>
      </div>
      <script>
        (function(){
          function doPrint(){ try { window.focus(); window.print(); } catch(e){} }
          if (document.readyState === 'complete') setTimeout(doPrint, 300); else window.addEventListener('load', () => setTimeout(doPrint, 300));
        })();
      </script>
      </body></html>`;
  };

  // Print Preview modal state
  const [printOpen, setPrintOpen] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [printCtx, setPrintCtx] = useState({ payment: null, history: [], invoiceIds: [] });

  const openPrint = async (paymentRow) => {
    setPrintOpen(true);
    setPrintLoading(true);
    try {
      const [detail, history] = await Promise.all([
        fetchJSON(`${API}/api/payments/${paymentRow.id}`),
        fetchJSON(`${API}/api/payments/${paymentRow.id}/history`)
      ]);
      const ids = new Set();
      const csv = String(detail?.invoiceIds || '').trim();
      if (csv) csv.split(',').map(s => s.trim()).filter(Boolean).forEach(s => ids.add(Number(s)));
      const legacy = Number(detail?.invoiceId || detail?.invoiceid || 0);
      if (Number.isFinite(legacy) && legacy > 0) ids.add(legacy);
      const invoiceIds = Array.from(ids).sort((a,b)=>a-b);
      setPrintCtx({ payment: detail, history, invoiceIds });
    } catch (e) {
      alert(e.message || 'Failed to load print preview');
    } finally {
      setPrintLoading(false);
    }
  };

  const printCurrent = () => {
    const html = buildPaymentPrintHtml(printCtx);
    const w = window.open('', '_blank', 'noopener,noreferrer');
    if (!w) { alert('Popup blocked. Please allow popups for this site.'); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  const downloadCurrentPdf = async () => {
    try {
      const id = printCtx?.payment?.id;
      if (!id) return;
      const res = await fetch(`${API}/api/payments/${id}/pdf`);
      if (!res.ok) throw new Error('Failed to generate PDF');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payment_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      alert(e.message || 'Download failed');
    }
  };

  return (
    <div className="payments-wrapper">
      {fatalError && (
        <div className="error-banner">
          Failed to load payments: {fatalError}
        </div>
      )}

      {/* Filters */}
      <div className="payments-filters">
        {/* Client NAME like Receipts */}
        <div className="search">
          <FaSearch />
          <input
            value={clientName}
            onChange={e => setClientName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                fetchPayments(0, limit);
              }
            }}
            placeholder="Client name"
          />
          {clientName && (
            <button className="clear" onClick={() => { setClientName(''); fetchPayments(0, limit); }}><FaTimes/></button>
          )}
        </div>

        {/* Invoice/Payment # (debounced text input) */}
        <div className="search search--inline" title="Filter by invoice or payment number">
          <FaSearch />
          <input
            type="text"
            className="rd-input"
            value={invoiceid}
            onChange={(e) => setInvoiceid(e.target.value)}
            placeholder="Invoice #, Payment #"
          />
          {invoiceid && (
            <button className="clear" onClick={() => setInvoiceid('')}>
              <FaTimes />
            </button>
          )}
      </div>

      {/* Date presets + from/to (Receipts-style) */}
      <div className="date-filters">
          <label className="preset">
            <select
              className="rd-input rd-select"
              value={datePreset}
              onChange={(e) => applyDatePreset(e.target.value)}
              title="Quick date ranges"
            >
              <option value="">Custom…</option>
              <option value="all">All</option>
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="this_week">This Week</option>
              <option value="last_week">Last Week</option>
              <option value="this_month">This Month</option>
              <option value="last_month">Last Month</option>
              <option value="this_year">This Year</option>
              <option value="last_year">Last Year</option>
            </select>
          </label>

          <div className="date-input from">
            <span className="date-label">From</span>
            <input
              type="date"
              className="rd-input"
              value={from}
              onChange={e => {
                setFrom(e.target.value);
                setDatePreset(''); localStorage.setItem('paymentsDatePreset', '');
                fetchPayments(0, limit);
              }}
            />
          </div>

          <div className="date-input to">
            <span className="date-label">To</span>
            <input
              type="date"
              className="rd-input"
              value={to}
              onChange={e => {
                setTo(e.target.value);
                setDatePreset(''); localStorage.setItem('paymentsDatePreset', '');
                fetchPayments(0, limit);
              }}
            />
        </div>
      </div>

      {/* Payment Type filter */}
      <label className="search" title="Filter by payment type">
        <span style={{ marginRight: 6 }}>Type</span>
        <select
          className="rd-input rd-select"
          value={paymentTypeFilter}
          onChange={(e) => { setPaymentTypeFilter(e.target.value); fetchPayments(0, limit); }}
        >
          <option value="">All</option>
          {paymentTypes.map(pt => (
            <option key={pt.id} value={pt.id}>{pt.name}</option>
          ))}
        </select>
      </label>

        <div className="search">
          <FaSearch />
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                fetchPayments(0, limit);
              }
            }}
            placeholder="Search ref/notes/user/client"
          />
          {q && (
            <button className="clear" onClick={() => { setQ(''); fetchPayments(0, limit); }}>
              <FaTimes />
            </button>
          )}
        </div>

        <label>
          Deleted:
          <select value={deleted} onChange={(e) => setDeleted(e.target.value)}>
            <option value="0">No</option>
            <option value="1">Yes</option>
          </select>
        </label>

        <button className="btn" onClick={() => fetchPayments(0, limit)}>Run</button>
        <button className="btn" onClick={exportCsv}>Export CSV</button>
        <button className="btn" onClick={openNew}>New Payment</button>
      </div>

      <div className="totals-bar">
        <div><strong>Total records:</strong> {meta.total}</div>
        <div><strong>Sum (amt):</strong> {money(meta.sumAmt)}</div>
      </div>

      {loading ? (
        <div className="loading">Loading payments…</div>
      ) : (
        <>
          <div className="payments-table-wrapper">
            <table className="payments-table">
              <thead>
                <tr>
                  <th>Actions</th>
                  {renderSortHeader('Payment #', 'id')}
                  {renderSortHeader('Client', 'clientName')}
                  {renderSortHeader('Date', 'date')}
                  {renderSortHeader('Invoice #', 'invoiceId')}
                  {renderSortHeader('Type', 'paymentTypeName')}
                  {renderSortHeader('Amount', 'amt')}
                  <th>Reference</th>
                  {renderSortHeader('Created by', 'userFullName')}
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {rows.length ? rows.map(p => {
                  const invIdRaw = p.invoiceId ?? p.invoiceid;
                  const userDisplay = p.userFullName || p.user;

                  // derive IDs list (mapping + legacy)
                  const idsCsv = (p.invoiceIds || '').trim();
                  const ids = idsCsv
                    ? idsCsv.split(',').map(s => s.trim()).filter(Boolean)
                    : (invIdRaw && Number(invIdRaw) > 0 ? [String(invIdRaw)] : []);

                  const showInline = ids.slice(0, 2);
                  const overflow = ids.slice(2);

                  return (
                    <tr key={p.id}>
                      {/* Actions (icons) */}
                      <td className="td-actions" style={{ whiteSpace: 'nowrap' }}>
                        {!p.deleted ? (
                          <>
                            <button
                              className="mini-btn"
                              title="Edit"
                              onClick={() => openEdit(p)}
                            >
                              <FaEdit size={16}/>
                            </button>
                            <button
                              className="mini-btn"
                              title="Print"
                              onClick={() => openPrint(p)}
                            >
                              <FaPrint size={16}/>
                            </button>
                            <button
                              className="mini-btn"
                              title="History"
                              onClick={() => openHistory(p)}
                            >
                              <FaHistory size={16}/>
                            </button>
                            <button
                              className="mini-btn danger"
                              title="Delete"
                              onClick={() => deletePayment(p.id)}
                            >
                              <FaTrash size={16}/>
                            </button>
                          </>
                        ) : (
                          <button
                            className="mini-btn"
                            title="Restore"
                            style={{ color: 'green' }}
                            onClick={() => restorePayment(p.id)}
                          >
                            <FaUndo size={16}/>
                          </button>
                        )}
                      </td>

                      {/* Payment ID */}
                      <td className="nowrap">{p.id}</td>

                      {/* Client */}
                      <td className="nowrap">{p.clientName}</td>

                      {/* Date (no time) */}
                      <td>{p.date ? new Date(p.date).toLocaleDateString() : ''}</td>

                      {/* Invoice # with hover popover for overflow */}
                      <td className="nowrap">
                        <div
                          style={popoverStyles.container}
                          onMouseEnter={() => startHover(p.id)}
                          onMouseLeave={endHover}
                        >
                          {ids.length === 0 ? (
                            'None'
                          ) : (
                            <>
                              {showInline.map((id, idx) => (
                                <React.Fragment key={id}>
                                  <Link className="invoice-link" to={`/invoice/${id}`}>#{id}</Link>
                                  {idx < showInline.length - 1 ? ', ' : ''}
                                </React.Fragment>
                              ))}
                              {overflow.length > 0 && (
                                <>
                                  {showInline.length ? ', ' : ''}
                                  <span style={popoverStyles.faded}>…</span>
                                </>
                              )}
                            </>
                          )}

                          {hoverPayId === p.id && overflow.length > 0 && (
                            <div
                              style={popoverStyles.bubble}
                              onMouseEnter={() => startHover(p.id)}
                              onMouseLeave={endHover}
                            >
                              <div style={{ fontWeight: 600, marginBottom: 6 }}>Applied to invoices</div>
                              <div>
                                {ids.map(id => (
                                  <Link
                                    key={id}
                                    className="invoice-link"
                                    to={`/invoice/${id}`}
                                    style={popoverStyles.chip}
                                  >
                                    #{id}
                                  </Link>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Type, Amount, Reference, Created by, Notes */}
                      <td>{p.paymentTypeName || paymentTypeNameById(p.paymenttype)}</td>
                      <td className='num'>{money(p.amt)}</td>
                      <td>{p.reference}</td>
                      <td>{userDisplay}</td>
                      <td>{p.notes}</td>
                    </tr>
                  );
                }) : (
                  <tr>
                    <td colSpan={10}>No payments found.</td>
                  </tr>
                )}
              </tbody>

              {page === lastPage && (
                <tfoot><tr className="totals-row"><td colSpan="6"></td><td className="num"><strong>{money(meta.sumAmt)}</strong></td><td colSpan="3"></td></tr></tfoot>
              )}


            </table>
          </div>

          {/* Pagination (Receipts-style) */}
          <div className="payments-pagination">
            <button className="btn" disabled={page <= 1} onClick={() => gotoPage(1)}>&laquo;</button>
            <button className="btn" disabled={page <= 1} onClick={() => gotoPage(page - 1)}>Prev</button>
            <span>Page {page} of {lastPage}</span>
            <button className="btn" disabled={page >= lastPage} onClick={() => gotoPage(page + 1)}>Next</button>
            <button className="btn" disabled={page >= lastPage} onClick={() => gotoPage(lastPage)}>&raquo;</button>
            <label style={{ marginLeft: 12, fontSize: 13 }}>
              Rows per page:&nbsp;
              <input
                type="number"
                min={1}
                max={500}
                value={limitInput}
                style={{ width: 60, fontSize: 13, padding: "2px 4px" }}
                onKeyDown={e => {
                  setIsTypingLimit(true);
                  if (e.key === 'Enter') {
                    setIsTypingLimit(false);
                    handleLimitUpdate(e.currentTarget.value);
                  }
                }}
                onChange={e => {
                  setLimitInput(e.target.value);
                  if (!isTypingLimit) {
                    handleLimitUpdate(e.target.value);
                  }
                }}
                onBlur={e => {
                  setIsTypingLimit(false);
                  handleLimitUpdate(e.target.value);
                }}
              />
            </label>
          </div>
        </>
      )}

      {/* Print Preview Modal */}
      {printOpen && (
        <div className="modal-overlay" onMouseDown={(e)=>{ if (e.target===e.currentTarget) setPrintOpen(false); }}>
          <div className="modal" role="dialog" aria-modal="true" aria-label="Print Preview" onMouseDown={(e)=>e.stopPropagation()}>
            <h2 style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <span>Payment Print Preview{printCtx?.payment?.id ? ` #${printCtx.payment.id}` : ''}</span>
              <button className="mini-btn" onClick={()=>setPrintOpen(false)} title="Close"><FaTimes/></button>
            </h2>
            {printLoading ? (
              <div>Loading…</div>
            ) : printCtx?.payment ? (
              <div className="modal-scroll">
                <div style={{ display:'grid', gridTemplateColumns:'repeat(2, minmax(0,1fr))', gap:'6px 12px', marginBottom: 8 }}>
                  <div><strong>Client:</strong> {printCtx.payment.clientName} (#{printCtx.payment.clientid})</div>
                  <div><strong>Date:</strong> {printCtx.payment.date ? new Date(printCtx.payment.date).toLocaleString() : ''}</div>
                  <div><strong>Amount:</strong> {money(printCtx.payment.amt)}</div>
                  <div><strong>Type:</strong> {printCtx.payment.paymentTypeName || paymentTypeNameById(printCtx.payment.paymenttype)}</div>
                  <div><strong>Reference:</strong> {printCtx.payment.reference || ''}</div>
                  <div><strong>Created by:</strong> {printCtx.payment.userFullName || printCtx.payment.user || ''}</div>
                  <div style={{ gridColumn: '1 / -1' }}><strong>Notes:</strong> {printCtx.payment.notes || ''}</div>
                </div>

                <h3>Applied Invoices</h3>
                {printCtx.invoiceIds?.length ? (
                  <ul style={{ marginTop: 6 }}>
                    {printCtx.invoiceIds.map(id => (<li key={id}>Invoice #{id}</li>))}
                  </ul>
                ) : (
                  <div style={{ opacity: .8 }}>None</div>
                )}

                <h3 style={{ marginTop: 12 }}>History</h3>
                <div className="table-wrapper">
                  <table className="table">
                    <thead>
                      <tr><th>Date</th><th>User</th><th>Log</th></tr>
                    </thead>
                    <tbody>
                      {printCtx.history?.length ? printCtx.history.map(h => (
                        <tr key={h.id}><td>{h.date ? new Date(h.date).toLocaleString() : ''}</td><td>{h.userFullName || ''}</td><td>{h.log || ''}</td></tr>
                      )) : (
                        <tr><td colSpan={3}>No history</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div>No data.</div>
            )}

            <div className="modal-actions" style={{ justifyContent:'flex-end' }}>
              <button className="btn" onClick={printCurrent}><FaPrint style={{ marginRight: 6 }}/> Print</button>
              <button className="btn" onClick={downloadCurrentPdf}><FaDownload style={{ marginRight: 6 }}/> Download PDF</button>
              <button className="btn" onClick={()=>setPrintOpen(false)}><FaTimes style={{ marginRight: 6 }}/> Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit/New Modal */}
      {editOpen && (
        <div className="modal-overlay">
          <div className="modal payment-edit">
            <div className="modal-head">
              <h2>{form.id ? `Edit Payment #${form.id}` : 'New Payment'}</h2>
              <div className="meta">
                <span className="muted">Created by</span>
                <strong>{form.userFullName || form.user || getUserFullName() || ''}</strong>
              </div>
            </div>

            <form className="payment-form" onSubmit={savePayment}>
              <div className="form-grid form-grid--2col">
                {/* NEW payment: choose client/invoice. EDIT: show summary */}
                {!form.id ? (
                  <>
                    <div className="field">
                      <span className="label">Client</span>
                      <select
                        id="clientid"
                        value={form.clientid}
                        onChange={(e) => {
                          const cid = e.target.value;
                          setForm({ ...form, clientid: cid, invoiceid: '' });
                        }}
                      >
                        <option value="">-- Select client --</option>
                        {clients.map(cl => (
                          <option key={cl.id} value={cl.id}>{cl.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="field">
                      <span className="label">Invoice (open balance)</span>
                      <select
                        id="invoiceid"
                        value={form.invoiceid || ''}
                        onChange={(e) => setForm({ ...form, invoiceid: e.target.value })}
                        disabled={!form.clientid}
                      >
                        <option value="">— None —</option>
                        {openInvoices.map(inv => (
                          <option key={inv.invoiceId} value={inv.invoiceId}>
                            #{inv.invoiceId} — {new Date(inv.invoiceDate).toLocaleDateString()} — Due: $
                            {Number(inv.amountDue).toFixed(2)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="field">
                      <span className="label">Client</span>
                      <div className="value">
                        {(() => {
                          const cl = clients.find(c => String(c.id) === String(form.clientid));
                          return cl ? `${cl.name} (#${form.clientid})` : `#${form.clientid}`;
                        })()}
                      </div>
                    </div>

                    <div className="field">
                      <span className="label">Invoice</span>
                      <div className="value">{displayInvoiceId(form.invoiceid)}</div>
                    </div>
                  </>
                )}

                <div className="field">
                  <span className="label">Date</span>
                  <input
                    id="date"
                    type="date"
                    value={form.date}
                    onChange={e => setForm({ ...form, date: e.target.value })}
                  />
                </div>

                {/* Payment type by NAME */}
                <div className="field">
                  <span className="label">Payment Type</span>
                  <select
                    id="paymenttype"
                    value={form.paymenttype}
                    onChange={e => setForm({ ...form, paymenttype: e.target.value })}
                  >
                    <option value="">-- Select --</option>
                    {paymentTypes.map((pt) => (
                      <option key={pt.id} value={pt.id}>{pt.name}</option>
                    ))}
                  </select>
                </div>

                <div className="field field--amount">
                  <span className="label">Amount</span>
                  <input
                    id="amt"
                    className="amount-input"
                    type="number"
                    step="0.01"
                    value={form.amt}
                    onChange={e => setForm({ ...form, amt: e.target.value })}
                  />
                </div>

                <div className="field">
                  <span className="label">Reference</span>
                  <input
                    id="reference"
                    type="text"
                    value={form.reference}
                    onChange={e => setForm({ ...form, reference: e.target.value })}
                  />
                </div>

                <div className="field field--notes">
                  <span className="label">Notes</span>
                  <input
                    id="notes"
                    type="text"
                    value={form.notes}
                    onChange={e => setForm({ ...form, notes: e.target.value })}
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn" disabled={!form.id && !form.clientid}>Save</button>
                <button type="button" className="btn" onClick={closeEdit}>Cancel</button>
              </div>
            </form>

            {form.id && (
              <div className="history-section">
                <h3>History</h3>
                {historyLoading ? (
                  <div className="loading">Loading history…</div>
                ) : (
                  <div className="table-wrapper">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>User</th>
                          <th>Log</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.length ? history.map(h => (
                          <tr key={h.id}>
                            <td>{h.date ? new Date(h.date).toLocaleString() : ''}</td>
                            <td>{h.userFullName || ''}</td>
                            <td>{h.log}</td>
                          </tr>
                        )) : (
                          <tr><td colSpan={3}>No history entries</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* History Modal */}
      {historyOpen && (
        <div className="modal-overlay" onClick={() => setHistoryOpen(false)}>
          <div className="modal" role="dialog" aria-modal="true" aria-labelledby="paymentHistoryTitle" onClick={(e) => e.stopPropagation()}>
            <h2 id="paymentHistoryTitle">Payment History #{historyForId}</h2>
            {historyLoading ? (
              <div className="loading">Loading history…</div>
            ) : (
              <div className="table-wrapper">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>User</th>
                      <th>Log</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.length ? history.map((h) => (
                      <tr key={h.id}>
                        <td>{h.date ? new Date(h.date).toLocaleString() : ''}</td>
                        <td>{h.userFullName || ''}</td>
                        <td>{h.log || ''}</td>
                      </tr>
                    )) : (
                      <tr><td colSpan={3}>No history entries</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
            <div className="modal-actions">
              <button className="btn" onClick={() => { if (historyForId) fetchHistory(historyForId); }}>Refresh</button>
              <button className="btn" onClick={() => setHistoryOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Payments;
